import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Users, Gauge, Clock, MapPin, Plane, Filter, Check, Info, Shield, Briefcase, Coffee, ChevronDown, ArrowRight, X } from 'lucide-react';
import type { Location } from '../types';

interface JetCategory {
  id: string;
  name: string;
  description: string;
  priceRange: string;
  capacity: string;
  range: string;
  speed: string;
  imageUrl: string;
  passengerCount: number; // Added for filtering
}

const jetCategories: JetCategory[] = [
  {
    id: 'vlj',
    name: 'Very Light Jet (VLJ)',
    description: 'Compact and efficient for short trips',
    priceRange: '4,250–5,750 €/h',
    capacity: '4-6 passengers',
    passengerCount: 6,
    range: '1,500-2,500 km',
    speed: '600-700 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'light',
    name: 'Light Jet',
    description: 'Perfect balance of comfort and efficiency',
    priceRange: '5,750–7,250 €/h',
    capacity: '6-8 passengers',
    passengerCount: 8,
    range: '2,500-3,500 km',
    speed: '700-800 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1583373834259-46cc92173cb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'super-light',
    name: 'Super Light Jet',
    description: 'Enhanced range with light jet efficiency',
    priceRange: '4,500–6,750 €/h',
    capacity: '7-9 passengers',
    passengerCount: 9,
    range: '3,000-4,000 km',
    speed: '750-850 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'midsize',
    name: 'Midsize Jet',
    description: 'Spacious cabin with transcontinental range',
    priceRange: '6,750–8,250 €/h',
    capacity: '8-10 passengers',
    passengerCount: 10,
    range: '4,000-5,000 km',
    speed: '800-850 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1583373834259-46cc92173cb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'super-midsize',
    name: 'Super Midsize Jet',
    description: 'Combines cabin space with impressive range',
    priceRange: '8,250–9,750 €/h',
    capacity: '8-12 passengers',
    passengerCount: 12,
    range: '5,000-6,500 km',
    speed: '850-900 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'heavy',
    name: 'Heavy Jet',
    description: 'Luxurious cabin with intercontinental range',
    priceRange: '9,750–12,750 €/h',
    capacity: '10-16 passengers',
    passengerCount: 16,
    range: '6,500-9,000 km',
    speed: '850-950 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1583373834259-46cc92173cb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'ultra-long-range',
    name: 'Ultra Long Range Jet',
    description: 'Global range with ultimate luxury',
    priceRange: '12,750–15,500 €/h',
    capacity: '12-19 passengers',
    passengerCount: 19,
    range: '9,000-14,000 km',
    speed: '900-1,000 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  },
  {
    id: 'vip',
    name: 'VIP / Head-of-State',
    description: 'Ultimate luxury and customization',
    priceRange: '15,500+ €/h',
    capacity: '15-50 passengers',
    passengerCount: 50,
    range: '10,000-15,000 km',
    speed: '900-1,000 km/h',
    imageUrl: 'https://images.unsplash.com/photo-1583373834259-46cc92173cb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'
  }
];

interface JetCategorySelectorProps {
  selectedCategory: string | undefined;
  onSelect: (categoryId: string) => void;
  requiredCapacity: number;
  distance: number;
  onBack: () => void;
  isFullscreen?: boolean;
  origin?: Location | null;
  destination?: Location | null;
  onRequestQuote: (category: JetCategory) => void;
}

export default function JetCategorySelector({ 
  selectedCategory, 
  onSelect, 
  requiredCapacity, 
  distance, 
  onBack,
  isFullscreen = false,
  origin,
  destination,
  onRequestQuote
}: JetCategorySelectorProps) {
  const [loading, setLoading] = useState(true);
  const [showCategories, setShowCategories] = useState(false);
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState<JetCategory | null>(null);
  const [filteredCategories, setFilteredCategories] = useState<JetCategory[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    minCapacity: requiredCapacity,
    maxPrice: Infinity
  });
  const contentRef = useRef<HTMLDivElement>(null);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      setShowCategories(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Filter categories based on passenger requirements
  useEffect(() => {
    const filtered = jetCategories.filter(category => {
      // Filter by capacity
      if (category.passengerCount < filters.minCapacity) {
        return false;
      }
      
      // Filter by price range (if implemented)
      // This would need price as a number rather than a string range
      
      return true;
    });
    
    setFilteredCategories(filtered);
  }, [filters, requiredCapacity]);

  const formatDistance = (km: number): string => {
    const miles = Math.round(km * 0.621371);
    return `${Math.round(km)} km (${miles} mi)`;
  };

  // Calculate estimated price based on distance and category
  const calculateEstimatedPrice = (category: JetCategory): string => {
    // Extract the average hourly rate from the price range
    const priceRange = category.priceRange.replace(/[^\d–]/g, '').split('–');
    let avgHourlyRate = 0;
    
    if (priceRange.length === 2) {
      avgHourlyRate = (parseInt(priceRange[0]) + parseInt(priceRange[1])) / 2;
    } else if (priceRange.length === 1) {
      avgHourlyRate = parseInt(priceRange[0]);
    } else {
      return 'Price on request';
    }
    
    // Extract average speed from the speed range
    const speedRange = category.speed.replace(/[^\d-]/g, '').split('-');
    let avgSpeed = 0;
    
    if (speedRange.length === 2) {
      avgSpeed = (parseInt(speedRange[0]) + parseInt(speedRange[1])) / 2;
    } else if (speedRange.length === 1) {
      avgSpeed = parseInt(speedRange[0]);
    } else {
      return 'Price on request';
    }
    
    // Calculate flight time in hours
    const flightTimeHours = distance / avgSpeed;
    
    // Add 1 hour for takeoff, landing, and taxiing
    const totalTimeHours = flightTimeHours + 1;
    
    // Calculate total price
    const totalPrice = avgHourlyRate * totalTimeHours;
    
    // Format the price
    return `Est. €${Math.round(totalPrice).toLocaleString()}`;
  };

  const getLocationDetails = (location?: Location | null) => {
    if (!location?.address) return { city: '', code: '' };
    const parts = location.address.split('(');
    return {
      city: parts[0].trim(),
      code: parts[1]?.replace(')', '') || ''
    };
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-6 border-b border-gray-100 shadow-sm">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 text-gray-600"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h2 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
          {origin && destination ? (
            <span className="hidden md:flex items-center gap-2">
              <span>{getLocationDetails(origin).city}</span>
              <ArrowRight size={16} className="text-gray-400" />
              <span>{getLocationDetails(destination).city}</span>
            </span>
          ) : (
            'Select Aircraft Category'
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Filter options"
          >
            <Filter size={20} className="text-gray-600" />
          </button>
        </div>
      </div>

      {origin && destination && (
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-sm text-gray-500">From</div>
                <div className="font-medium text-lg">{getLocationDetails(origin).city}</div>
                <div className="text-sm font-mono text-gray-600">{getLocationDetails(origin).code}</div>
              </div>
              <ArrowRight size={20} className="text-gray-400" />
              <div>
                <div className="text-sm text-gray-500">To</div>
                <div className="font-medium text-lg">{getLocationDetails(destination).city}</div>
                <div className="text-sm font-mono text-gray-600">{getLocationDetails(destination).code}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Distance</div>
              <div className="font-medium">{formatDistance(distance)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="px-6 py-4 border-b border-gray-100 bg-white">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Filter Options</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Passenger Capacity
              </label>
              <div className="flex items-center">
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={filters.minCapacity}
                  onChange={(e) => setFilters(prev => ({ ...prev, minCapacity: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 min-w-[40px] text-center">{filters.minCapacity}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>50</span>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setFilters({ minCapacity: requiredCapacity, maxPrice: Infinity })}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto" ref={contentRef}>
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-gray-600">Finding suitable aircraft categories...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Select Aircraft Category</h3>
                  <p className="text-gray-600">
                    {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'} available for {requiredCapacity} {requiredCapacity === 1 ? 'passenger' : 'passengers'}
                  </p>
                </div>
                
                {filteredCategories.length < jetCategories.length && (
                  <div className="text-sm text-gray-500">
                    Showing aircraft for {requiredCapacity}+ passengers
                  </div>
                )}
              </div>
              
              {filteredCategories.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Info size={24} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">No matching aircraft</h3>
                  <p className="text-gray-500 mt-2">
                    No aircraft categories match your passenger requirements.
                  </p>
                  <button 
                    onClick={() => setFilters(prev => ({ ...prev, minCapacity: 1 }))}
                    className="mt-4 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Show All Categories
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`relative p-4 rounded-xl border transition-all cursor-pointer ${
                        selectedCategory === category.id
                          ? 'border-black bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedCategoryDetails(category);
                        onSelect(category.id);
                      }}
                    >
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-24 h-20">
                          <img
                            src={category.imageUrl}
                            alt={category.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <div className="flex-grow">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-0">
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">{category.name}</h4>
                              <p className="text-sm text-gray-500">{category.description}</p>
                            </div>
                            <div className="text-lg font-semibold">{calculateEstimatedPrice(category)}</div>
                          </div>
                          
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              <Users size={14} />
                              {category.capacity}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                              <MapPin size={14} />
                              {category.range}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                              <Gauge size={14} />
                              {category.speed}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {selectedCategory === category.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestQuote(category);
                          }}
                          className="mt-3 w-full bg-black text-white py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                        >
                          Request Quote
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Category Details */}
              {selectedCategoryDetails && selectedCategory && (
                <div className="bg-gray-50 p-4 rounded-xl space-y-4 mt-6">
                  <h3 className="font-medium text-gray-900">{selectedCategoryDetails.name} Details</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Information</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Users size={14} className="text-gray-400" />
                          <span>{selectedCategoryDetails.capacity}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Gauge size={14} className="text-gray-400" />
                          <span>{selectedCategoryDetails.speed}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Plane size={14} className="text-gray-400" />
                          <span>{selectedCategoryDetails.range} range</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Briefcase size={14} className="text-gray-400" />
                          <span>Ample baggage space</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Flight Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Estimated Price</span>
                          <span>{calculateEstimatedPrice(selectedCategoryDetails)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Typical Flight Time</span>
                          <span>{Math.round(distance / parseInt(selectedCategoryDetails.speed.split('-')[0]))} hours</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Amenities</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Coffee size={14} className="text-gray-400" />
                          <span>Refreshments</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield size={14} className="text-gray-400" />
                          <span>Wi-Fi Available</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Info size={14} className="text-gray-400" />
                          <span>Entertainment</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}